import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import type { Comment } from "../lib/types";

export function useComments(slug: string) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    apiClient
      .get<Comment[]>(`/api/parties/${slug}/comments`)
      .then(setComments)
      .catch(() => setComments([]))
      .finally(() => setIsLoading(false));
  }, [slug]);

  const addComment = useCallback(
    async (body: string, parentId?: number) => {
      setIsSubmitting(true);
      try {
        const comment = await apiClient.post<Comment>(
          `/api/parties/${slug}/comments`,
          { body, ...(parentId !== undefined ? { parent_id: parentId } : {}) },
        );
        setComments((prev) => [comment, ...prev]);
      } finally {
        setIsSubmitting(false);
      }
    },
    [slug],
  );

  const editComment = useCallback(async (id: number, body: string) => {
    await apiClient.patch(`/api/comments/${id}`, { body });
    setComments((prev) => prev.map((c) => (c.id === id ? { ...c, body } : c)));
  }, []);

  const deleteComment = useCallback(async (id: number) => {
    await apiClient.delete(`/api/comments/${id}`);
    setComments((prev) => {
      const idsToRemove = new Set<number>();
      const collect = (parentId: number) => {
        for (const c of prev) {
          if (c.parent_id === parentId) {
            idsToRemove.add(c.id);
            collect(c.id);
          }
        }
      };
      idsToRemove.add(id);
      collect(id);
      return prev.filter((c) => !idsToRemove.has(c.id));
    });
  }, []);

  return {
    comments,
    isLoading,
    isSubmitting,
    addComment,
    editComment,
    deleteComment,
  };
}
